import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeftIcon,
  CreditCardIcon,
  XCircleIcon,
  CheckCircleIcon,
  PencilIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import { useUser } from "../hooks/useUsers";
import {
  suspendUser,
  activateUser,
  updateUserCredits,
  updateUserPlan,
  logAdminActivity,
  adminDeleteUserAccount,
  getUserCreditsBalance,
} from "@/shared/db";
import { getUserDocuments } from "@/shared/db";
import { toast } from "sonner";
import { useAuth } from "../../../contexts/AuthContext";

export const UserDetailPage: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const { user, loading, error, refetch } = useUser(id);
  const [documents, setDocuments] = useState<any[]>([]);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [showAllDocs, setShowAllDocs] = useState(false);
  const [creditsFromUserCredits, setCreditsFromUserCredits] = useState<number | null>(null);
  const [showCreditsModal, setShowCreditsModal] = useState(false);
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [newCredits, setNewCredits] = useState(0);
  const [newPlan, setNewPlan] = useState<"free" | "pro">("free");

  useEffect(() => {
    if (user?.id) {
      loadDocuments();
      getUserCreditsBalance(user.id).then((b) => setCreditsFromUserCredits(b));
    } else {
      setCreditsFromUserCredits(null);
    }
  }, [user?.id]);

  const loadDocuments = async () => {
    if (!user?.id) return;
    setLoadingDocuments(true);
    try {
      const docs = await getUserDocuments(user.id);
      setDocuments(docs);
    } catch (err) {
      console.error("Error loading documents:", err);
    } finally {
      setLoadingDocuments(false);
    }
  };

  const handleSuspend = async () => {
    if (!user?.id || !currentUser?.id) return;
    if (window.confirm(t("admin.users.detail.actions.suspendConfirm"))) {
      try {
        await suspendUser(user.id);
        await logAdminActivity(
          currentUser.id,
          "suspend_user",
          "user",
          user.id,
          { reason: "Admin action" }
        );
        toast.success(t("admin.users.actions.suspended"));
        refetch();
      } catch (err) {
        toast.error(t("admin.errors.suspendFailed"));
      }
    }
  };

  const handleActivate = async () => {
    if (!user?.id || !currentUser?.id) return;
    if (window.confirm(t("admin.users.detail.actions.activateConfirm"))) {
      try {
        await activateUser(user.id);
        await logAdminActivity(
          currentUser.id,
          "activate_user",
          "user",
          user.id,
          { reason: "Admin action" }
        );
        toast.success(t("admin.users.actions.activated"));
        refetch();
      } catch (err) {
        toast.error(t("admin.errors.activateFailed"));
      }
    }
  };

  const handleUpdateCredits = async () => {
    if (!user?.id || !currentUser?.id) return;
    try {
      await updateUserCredits(user.id, newCredits, currentUser.id);
      await logAdminActivity(
        currentUser.id,
        "update_credits",
        "user",
        user.id,
        { credits: newCredits }
      );
      toast.success(t("admin.users.actions.creditsUpdated"));
      setShowCreditsModal(false);
      refetch();
    } catch (err) {
      toast.error(t("admin.errors.updateCreditsFailed"));
    }
  };

  const handleUpdatePlan = async () => {
    if (!user?.id || !currentUser?.id) return;
    try {
      await updateUserPlan(user.id, newPlan);
      await logAdminActivity(currentUser.id, "update_plan", "user", user.id, {
        plan: newPlan,
      });
      toast.success(t("admin.users.actions.planUpdated"));
      setShowPlanModal(false);
      refetch();
    } catch (err) {
      toast.error(t("admin.errors.updatePlanFailed"));
    }
  };

  const handleDeleteAccount = async () => {
    if (!user?.id || !currentUser?.id) return;
    if (!window.confirm(t("admin.users.detail.actions.deleteAccountConfirm"))) return;
    try {
      await adminDeleteUserAccount(user.id);
      await logAdminActivity(currentUser.id, "delete_user", "user", user.id, {
        target_email: user.email,
      });
      toast.success(t("admin.users.actions.accountDeleted"));
      navigate("/admin/users");
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : t("admin.errors.deleteAccountFailed")
      );
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-tiber-500"></div>
      </div>
    );
  }

  if (error || !user) {
    return (
      <div className="flex flex-col items-center justify-center min-h-96">
        <p className="text-red-600 mb-4">
          {error || t("admin.errors.fetchUserFailed")}
        </p>
        <button
          onClick={() => navigate("/admin/users")}
          className="px-4 py-2 bg-tiber-500 text-white rounded-lg hover:bg-tiber-500/90 transition-colors"
        >
          {t("admin.users.detail.back")}
        </button>
      </div>
    );
  }

  const effectivePlan =
    (user as { effective_plan?: string | null }).effective_plan || user.plan;
  const effectiveSubscriptionStatus = (
    user as { effective_subscription_status?: string | null }
  ).effective_subscription_status;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate("/admin/users")}
            className="p-2 rounded-lg hover:bg-romance-50 transition-colors"
          >
            <ArrowLeftIcon className="w-6 h-6 text-juridico" />
          </button>
          <div>
            <h1 className="text-3xl font-display font-bold text-juridico">
              {t("admin.users.detail.title")}
            </h1>
            <p className="text-juridico/60 mt-1">{user.email}</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Profile Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="lg:col-span-2 bg-white rounded-lg border border-romance-200 p-6"
        >
          <h2 className="text-xl font-bold text-juridico mb-6">
            {t("admin.users.detail.profile.title")}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="text-sm font-medium text-juridico/60">
                {t("admin.users.detail.profile.email")}
              </label>
              <p className="text-juridico font-medium mt-1">{user.email}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-juridico/60">
                {t("admin.users.detail.profile.name")}
              </label>
              <p className="text-juridico font-medium mt-1">
                {user.full_name || t("common.notAvailable")}
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-juridico/60">
                {t("admin.users.detail.profile.type")}
              </label>
              <p className="text-juridico font-medium mt-1 capitalize">
                {user.user_type || t("common.notAvailable")}
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-juridico/60">
                {t("admin.users.detail.profile.country")}
              </label>
              <p className="text-juridico font-medium mt-1">
                {user.country || t("common.notAvailable")}
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-juridico/60">
                {t("admin.users.detail.profile.status")}
              </label>
              <p className="mt-1">
                <span
                  className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                    user.is_active
                      ? "bg-atlantis-100 text-green-800"
                      : "bg-red-100 text-red-800"
                  }`}
                >
                  {user.is_active
                    ? t("admin.users.active")
                    : t("admin.users.suspended")}
                </span>
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-juridico/60">
                {t("admin.users.detail.profile.plan")}
              </label>
              <p className="mt-1">
                <span
                  className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                    effectivePlan === "lawyer_professional_pro" ||
                    effectivePlan === "lawyer_professional" ||
                    effectivePlan === "pro"
                      ? "bg-tiber-500/10 text-tiber-500"
                      : effectivePlan === "lawyer_trial"
                        ? "bg-yellow-100 text-yellow-800"
                      : "bg-tiber-100 text-tiber-800"
                  }`}
                >
                  {effectivePlan}
                </span>
              </p>
              {effectivePlan !== user.plan && (
                <p className="text-xs text-juridico/50 mt-0.5">
                  Source: subscriptions (effective)
                  {effectiveSubscriptionStatus
                    ? ` • status=${effectiveSubscriptionStatus}`
                    : ""}
                </p>
              )}
            </div>
            <div>
              <label className="text-sm font-medium text-juridico/60">
                {t("admin.users.detail.profile.credits")}
              </label>
              <p className="text-juridico font-medium mt-1">
                {Math.max(
                  creditsFromUserCredits ?? 0,
                  user?.credits_remaining ?? 0
                )}
              </p>
              {(creditsFromUserCredits !== null &&
                (user?.credits_remaining ?? 0) !== (creditsFromUserCredits ?? 0)) && (
                <p className="text-xs text-juridico/50 mt-0.5">
                  {t("admin.users.detail.profile.creditsSource", {
                    profiles: user?.credits_remaining ?? 0,
                    userCredits: creditsFromUserCredits ?? 0,
                  })}
                </p>
              )}
            </div>
            <div>
              <label className="text-sm font-medium text-juridico/60">
                {t("admin.users.detail.profile.createdAt")}
              </label>
              <p className="text-juridico font-medium mt-1">
                {new Date(user.created_at).toLocaleDateString()}
              </p>
            </div>
          </div>
        </motion.div>

        {/* Actions Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-lg border border-romance-200 p-6"
        >
          <h2 className="text-xl font-bold text-juridico mb-6">
            {t("admin.users.detail.actions.title")}
          </h2>
          <div className="space-y-3">
            {user.is_active ? (
              <button
                onClick={handleSuspend}
                className="w-full flex items-center space-x-2 px-4 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors"
              >
                <XCircleIcon className="w-5 h-5" />
                <span>{t("admin.users.detail.actions.suspend")}</span>
              </button>
            ) : (
              <button
                onClick={handleActivate}
                className="w-full flex items-center space-x-2 px-4 py-2 bg-atlantis-50 text-atlantis-600 rounded-lg hover:bg-atlantis-100 transition-colors"
              >
                <CheckCircleIcon className="w-5 h-5" />
                <span>{t("admin.users.detail.actions.activate")}</span>
              </button>
            )}
            <button
              onClick={() => {
                setNewCredits(
                  Math.max(
                    creditsFromUserCredits ?? 0,
                    user?.credits_remaining ?? 0
                  )
                );
                setShowCreditsModal(true);
              }}
              className="w-full flex items-center space-x-2 px-4 py-2 bg-tiber-500/10 text-tiber-500 rounded-lg hover:bg-tiber-500/20 transition-colors"
            >
              <CreditCardIcon className="w-5 h-5" />
              <span>{t("admin.users.detail.actions.updateCredits")}</span>
            </button>
            <button
              onClick={() => {
                setNewPlan(user.plan as "free" | "pro");
                setShowPlanModal(true);
              }}
              className="w-full flex items-center space-x-2 px-4 py-2 bg-tiber-500/10 text-tiber-500 rounded-lg hover:bg-tiber-500/20 transition-colors"
            >
              <PencilIcon className="w-5 h-5" />
              <span>{t("admin.users.detail.actions.updatePlan")}</span>
            </button>
            <button
              onClick={handleDeleteAccount}
              className="w-full flex items-center space-x-2 px-4 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors"
            >
              <TrashIcon className="w-5 h-5" />
              <span>{t("admin.users.detail.actions.deleteAccount")}</span>
            </button>
          </div>
        </motion.div>
      </div>

      {/* Documents Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-white rounded-lg border border-romance-200 p-6"
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-juridico">
            {t("admin.users.detail.documents.title")}
          </h2>
          <span className="text-sm text-juridico/60">
            {t("admin.users.detail.documents.total", {
              count: documents.length,
            })}
          </span>
        </div>

        {loadingDocuments ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-tiber-500"></div>
          </div>
        ) : documents.length === 0 ? (
          <div className="flex items-center justify-center py-12">
            <p className="text-juridico/60">
              {t("admin.users.detail.documents.noDocuments")}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-romance-200">
              <thead className="bg-romance-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-juridico uppercase tracking-wider">
                    {t("admin.users.detail.documents.table.title")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-juridico uppercase tracking-wider">
                    {t("admin.users.detail.documents.table.type")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-juridico uppercase tracking-wider">
                    {t("admin.users.detail.documents.table.country")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-juridico uppercase tracking-wider">
                    {t("admin.users.detail.documents.table.status")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-juridico uppercase tracking-wider">
                    {t("admin.users.detail.documents.table.created")}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-romance-200">
                {(showAllDocs ? documents : documents.slice(0, 20)).map((doc) => (
                  <tr
                    key={doc.id}
                    className="hover:bg-romance-50 transition-colors"
                  >
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-juridico">
                        {doc.title}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-juridico">
                      {doc.document_type}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-juridico">
                      {doc.country}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          doc.status === "final"
                            ? "bg-atlantis-100 text-green-800"
                            : "bg-yellow-100 text-yellow-800"
                        }`}
                      >
                        {doc.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-juridico/60">
                      {new Date(doc.created_at).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {documents.length > 20 && (
              <div className="px-6 py-3 border-t border-romance-200 text-center">
                <button
                  onClick={() => setShowAllDocs((v) => !v)}
                  className="text-sm text-tiber-500 hover:text-tiber-500/80 font-medium"
                >
                  {showAllDocs
                    ? t("common.close")
                    : t("common.viewAll") + ` (${documents.length})`}
                </button>
              </div>
            )}
          </div>
        )}
      </motion.div>

      {/* Credits Modal */}
      {showCreditsModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-lg p-6 max-w-md w-full mx-4"
          >
            <h3 className="text-xl font-bold text-juridico mb-4">
              {t("admin.users.detail.actions.updateCreditsTitle")}
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-juridico mb-2">
                  {t("admin.users.detail.actions.newCredits")}
                </label>
                <input
                  type="number"
                  value={newCredits}
                  onChange={(e) => setNewCredits(parseInt(e.target.value) || 0)}
                  className="w-full px-4 py-2 border border-romance-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-tiber-500"
                />
              </div>
              <div className="flex space-x-3">
                <button
                  onClick={handleUpdateCredits}
                  className="flex-1 px-4 py-2 bg-tiber-500 text-white rounded-lg hover:bg-tiber-500/90 transition-colors"
                >
                  {t("common.save")}
                </button>
                <button
                  onClick={() => setShowCreditsModal(false)}
                  className="flex-1 px-4 py-2 border border-romance-200 text-juridico rounded-lg hover:bg-romance-50 transition-colors"
                >
                  {t("common.cancel")}
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Plan Modal */}
      {showPlanModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-lg p-6 max-w-md w-full mx-4"
          >
            <h3 className="text-xl font-bold text-juridico mb-4">
              {t("admin.users.detail.actions.updatePlanTitle")}
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-juridico mb-2">
                  {t("admin.users.detail.actions.newPlan")}
                </label>
                <select
                  value={newPlan}
                  onChange={(e) => setNewPlan(e.target.value as "free" | "pro")}
                  className="w-full px-4 py-2 border border-romance-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-tiber-500"
                >
                  <option value="free">Free</option>
                  <option value="pro">Pro</option>
                </select>
              </div>
              <div className="flex space-x-3">
                <button
                  onClick={handleUpdatePlan}
                  className="flex-1 px-4 py-2 bg-tiber-500 text-white rounded-lg hover:bg-tiber-500/90 transition-colors"
                >
                  {t("common.save")}
                </button>
                <button
                  onClick={() => setShowPlanModal(false)}
                  className="flex-1 px-4 py-2 border border-romance-200 text-juridico rounded-lg hover:bg-romance-50 transition-colors"
                >
                  {t("common.cancel")}
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};
